import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { orderService } from "../services";
import defaultImage from "../assets/images/cuisines/default.jpeg";

type ActiveGroupSession = {
  _id: string;
  name?: string;
  state?: string;
  expiresAt: string;
  members?: unknown[];
  restaurant?: {
    businessName?: string;
    kitchenPhotos?: string[];
  };
};

/** Aligned with mobile App/Components/UI/GroupOrderBanner.tsx */
const GroupOrderBanner = () => {
  const navigate = useNavigate();
  const [session, setSession] = useState<ActiveGroupSession | null>(null);

  useEffect(() => {
    const fetchSession = async () => {
      try {
        const res: any = await orderService.getOwnGroupSessions();
        const data = res?.data;
        if (!res?.status || !data) {
          setSession(null);
          return;
        }
        const expiresAt = new Date(data.expiresAt).getTime();
        const isActive =
          expiresAt > Date.now() &&
          String(data.state || "DRAFT").toUpperCase() === "DRAFT";
        setSession(isActive ? data : null);
      } catch {
        setSession(null);
      }
    };

    fetchSession();
    const interval = window.setInterval(fetchSession, 30000);
    return () => window.clearInterval(interval);
  }, []);

  if (!session) return null;

  const memberCount = session.members?.length || 0;
  const remainingMinutes = Math.max(
    0,
    Math.floor((new Date(session.expiresAt).getTime() - Date.now()) / 60000)
  );
  const restaurantImage =
    session.restaurant?.kitchenPhotos?.[0] || defaultImage;
  const restaurantName = session.restaurant?.businessName || "Restaurant";
  const groupName = session.name || "Group Order";

  return (
    <button
      type="button"
      onClick={() => navigate(`/group-order/${session._id}`)}
      className="fixed left-3 right-3 md:left-1/2 md:right-auto md:-translate-x-1/2 md:w-[420px] bottom-24 md:bottom-8 z-[90] flex items-center justify-between gap-3 rounded-[12px] bg-[#6366F1] text-white px-4 py-3.5 shadow-lg text-left"
    >
      <div className="flex items-center gap-3 min-w-0">
        <img
          src={restaurantImage}
          alt={restaurantName}
          className="h-11 w-11 rounded-full object-cover shrink-0"
          onError={(e) => {
            e.currentTarget.src = defaultImage;
          }}
        />
        <div className="min-w-0">
          <p className="font-urbanist text-sm font-semibold truncate">
            {restaurantName}
          </p>
          <p className="font-urbanist text-xs opacity-90 truncate">
            {groupName} · {memberCount}{" "}
            {memberCount === 1 ? "member" : "members"}
          </p>
          <p className="font-urbanist text-[11px] opacity-80">
            Expires in {remainingMinutes}m
          </p>
        </div>
      </div>
      <span className="font-urbanist text-sm font-semibold whitespace-nowrap">
        View Group →
      </span>
    </button>
  );
};

export default GroupOrderBanner;
